import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState } from "react";
import { authApi } from "@/api/auth";
import { ApiError } from "@/api/types";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/password-input";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/components/layout/auth-shell";

export const Route = createFileRoute("/reset-password")({
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      await authApi.resetPassword(token, password);
      setDone(true);
      // Reset invalidates all sessions — send them to sign in with the new one.
      setTimeout(() => void nav({ to: "/login" }), 1500);
    } catch (e) {
      if (e instanceof ApiError && e.status === 400) setErr("This reset link is invalid or has expired.");
      else setErr("Could not reset your password. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  if (!token) {
    return (
      <AuthShell title="Reset password" subtitle="Choose a new password.">
        <p role="alert" className="text-sm text-fail">
          This link is missing its reset token. Use the exact link from your email.
        </p>
        <Button asChild variant="ghost" className="mt-4 w-full">
          <Link to="/login">Back to sign in</Link>
        </Button>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell title="Password updated" subtitle="You're all set.">
        <p className="text-sm text-muted-foreground">Your password was changed. Redirecting you to sign in…</p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Reset password" subtitle="Choose a new password for your account.">
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="password">New password</Label>
          <PasswordInput
            id="password"
            required
            minLength={10}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        {err && (
          <p role="alert" className="text-sm text-fail">
            {err}
          </p>
        )}
        <Button
          type="submit"
          className="w-full bg-brand text-primary-foreground hover:bg-brand/90"
          disabled={loading}
        >
          {loading ? "Saving…" : "Set new password"}
        </Button>
      </form>
    </AuthShell>
  );
}
